// src/rokt/roktService.ts
// —————————————————————————————————————————————————————————————————————
// A simple singleton wrapper around window.Rokt.createLauncher()
// —————————————————————————————————————————————————————————————————————

import { ACCOUNT_ID } from "Constants/AlertModals";




/** Types for Rokt SDK objects **/
export interface RoktSelection {
    close(): void;
  }
  
  export interface RoktLauncher {
    selectPlacements(options: {
      attributes: Record<string, any>;
    }): Promise<RoktSelection>;
    terminate(): void;
  }
  
  /** Internal singleton promise */
  let launcherPromise: Promise<RoktLauncher> | null = null;
  
  /**
   * Ensure the Rokt script is injected into the <head>.
   * Returns a Promise that resolves when the script has loaded.
   */
  function loadScript(): Promise<void> {
    // If script already loaded
    if ((window as any).Rokt) {
      return Promise.resolve();
    }
  
    // If script is still loading via index.html, wait for it
    if ((window as any).RoktLauncherScriptPromise) {
      return (window as any).RoktLauncherScriptPromise;
    }
  
    // Script not found and not loading
    return Promise.reject(new Error("Rokt script not found in DOM and no promise available"));
  }
  
  
  /**
   * Initialize (or return) the singleton Rokt Launcher.
   *
   * @param accountId  your Rokt account ID
   */
  export function initRokt(
    accountId: string
  ): Promise<RoktLauncher> {
    if (!launcherPromise) {
      console.log('®️⭕KT::Calling Rokt');
      launcherPromise = loadScript()      
        .then(() => {
          // Create launcher after script is loaded
          return (window as any).Rokt.createLauncher({ accountId });
        })
        .catch((err) => {
          console.log('®️⭕KT::Rokt error', err);
          launcherPromise = null;
          throw err;
        });
    }
  
    console.log('®️⭕KT::Calling Rokt', launcherPromise);
    return launcherPromise;
  }
  
  
  /**
   * Trigger a placement by identifier.
   * Returns the RoktSelection, which you can close() later.
   */
  export async function showPlacement(
    attributes: Record<string, any>
  ): Promise<RoktSelection> {
    const launcher = await initRokt(ACCOUNT_ID);
    return launcher.selectPlacements({attributes});
  }
  
  /**
   * Tear down the Rokt singleton (e.g. on full app unmount or logout).
   */
  export async function terminateRokt(): Promise<void> {
    if (launcherPromise) {
      const launcher = await launcherPromise;
      launcher.terminate();     // :contentReference[oaicite:0]{index=0}
      launcherPromise = null;
    }
  }
  